import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Tag, Calendar, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";

const promoSchema = z.object({
  ad_name: z.string().trim().min(1, "Ad name is required").max(100),
  company_name: z.string().trim().min(1, "Company name is required").max(100),
  offer_text: z.string().trim().min(1, "Offer text is required").max(500),
  cta_url: z.string().trim().url("Must be a valid URL").max(500),
  image_url: z.string().trim().url("Must be a valid URL").max(500),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().min(1, "End date is required"),
  company_address: z.string().trim().max(200).optional(),
  contact_person: z.string().trim().max(100).optional(),
  contact_email: z.string().trim().email("Invalid email").max(100).optional().or(z.literal("")),
  contact_phone: z.string().trim().max(20).optional(),
  company_website: z.string().trim().url("Invalid URL").max(200).optional().or(z.literal("")),
  category: z.string().trim().max(50).optional(),
  placement: z.string().trim().max(50).optional(),
  page_placement: z.string().trim().min(1, "Page placement is required").max(50),
  budget_usd: z.string().optional(),
  cost_per_impression: z.string().optional(),
  cost_per_click: z.string().optional(),
});

interface Promotion {
  id: string;
  ad_name: string | null;
  company_name: string;
  offer_text: string | null;
  cta_url: string;
  image_url: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  status: string | null;
  company_address: string | null;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  company_website: string | null;
  category: string | null;
  placement: string | null;
  page_placement: string;
  target_locations: string[] | null;
  tags: string[] | null;
  budget_usd: number | null;
  cost_per_impression: number | null;
  cost_per_click: number | null;
  views: number | null;
  clicks: number | null;
  redemptions: number | null;
  created_at: string;
}

export default function AdminPromosManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [deletePromoId, setDeletePromoId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    ad_name: "",
    company_name: "",
    offer_text: "",
    cta_url: "",
    image_url: "",
    start_date: "",
    end_date: "",
    company_address: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    company_website: "",
    category: "",
    placement: "promos_locales",
    page_placement: "promos_locales",
    target_locations: "",
    tags: "",
    budget_usd: "",
    cost_per_impression: "",
    cost_per_click: "",
    is_active: true,
    status: "active" as "active" | "expired" | "paused",
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("ads")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPromotions(data || []);
    } catch (error) {
      console.error("Error fetching promotions:", error);
      toast({
        title: "Error",
        description: "Failed to load promotions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      ad_name: "",
      company_name: "",
      offer_text: "",
      cta_url: "",
      image_url: "",
      start_date: "",
      end_date: "",
      company_address: "",
      contact_person: "",
      contact_email: "",
      contact_phone: "",
      company_website: "",
      category: "",
      placement: "promos_locales",
      page_placement: "promos_locales",
      target_locations: "",
      tags: "",
      budget_usd: "",
      cost_per_impression: "",
      cost_per_click: "",
      is_active: true,
      status: "active" as "active" | "expired" | "paused",
    });
    setFormErrors({});
    setEditingPromo(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate form data
      const validatedData = promoSchema.parse(formData);
      
      const promoData = {
        ad_name: validatedData.ad_name,
        company_name: validatedData.company_name,
        offer_text: validatedData.offer_text,
        cta_url: validatedData.cta_url,
        image_url: validatedData.image_url,
        start_date: validatedData.start_date,
        end_date: validatedData.end_date,
        company_address: validatedData.company_address || null,
        contact_person: validatedData.contact_person || null,
        contact_email: validatedData.contact_email || null,
        contact_phone: validatedData.contact_phone || null,
        company_website: validatedData.company_website || null,
        category: validatedData.category || null,
        placement: validatedData.placement || null,
        page_placement: validatedData.page_placement,
        target_locations: formData.target_locations 
          ? formData.target_locations.split(",").map(loc => loc.trim()).filter(Boolean)
          : [],
        tags: formData.tags 
          ? formData.tags.split(",").map(tag => tag.trim()).filter(Boolean)
          : [],
        budget_usd: formData.budget_usd ? parseFloat(formData.budget_usd) : null,
        cost_per_impression: formData.cost_per_impression ? parseFloat(formData.cost_per_impression) : null,
        cost_per_click: formData.cost_per_click ? parseFloat(formData.cost_per_click) : null,
        is_active: formData.is_active,
        status: formData.status,
        created_by: user?.id,
      };

      if (editingPromo) {
        const { error } = await supabase
          .from("ads")
          .update(promoData)
          .eq("id", editingPromo.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Promotion updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("ads")
          .insert([promoData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Promotion created successfully",
        });
      }

      setDialogOpen(false);
      resetForm();
      fetchPromotions();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setFormErrors(errors);
        toast({
          title: "Validation Error",
          description: "Please check the form fields",
          variant: "destructive",
        });
      } else {
        console.error("Error saving promotion:", error);
        toast({
          title: "Error",
          description: "Failed to save promotion",
          variant: "destructive",
        });
      }
    }
  };

  const handleEdit = (promo: Promotion) => {
    setEditingPromo(promo);
    setFormData({
      ad_name: promo.ad_name || "",
      company_name: promo.company_name,
      offer_text: promo.offer_text || "",
      cta_url: promo.cta_url,
      image_url: promo.image_url,
      start_date: promo.start_date,
      end_date: promo.end_date,
      company_address: promo.company_address || "",
      contact_person: promo.contact_person || "",
      contact_email: promo.contact_email || "",
      contact_phone: promo.contact_phone || "",
      company_website: promo.company_website || "",
      category: promo.category || "",
      placement: promo.placement || "promos_locales",
      page_placement: promo.page_placement,
      target_locations: promo.target_locations?.join(", ") || "",
      tags: promo.tags?.join(", ") || "",
      budget_usd: promo.budget_usd?.toString() || "",
      cost_per_impression: promo.cost_per_impression?.toString() || "",
      cost_per_click: promo.cost_per_click?.toString() || "",
      is_active: promo.is_active,
      status: (promo.status || "active") as "active" | "expired" | "paused",
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletePromoId) return;

    try {
      const { error } = await supabase
        .from("ads")
        .delete()
        .eq("id", deletePromoId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Promotion deleted successfully",
      });

      fetchPromotions();
    } catch (error) {
      console.error("Error deleting promotion:", error);
      toast({
        title: "Error",
        description: "Failed to delete promotion",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setDeletePromoId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="p-6">Loading promotions...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Promotions Management</h1>
          <p className="text-muted-foreground">Create and manage promotional campaigns</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Promotion
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPromo ? "Edit Promotion" : "Create New Promotion"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="ad_name">Ad Name *</Label>
                  <Input
                    id="ad_name"
                    value={formData.ad_name}
                    onChange={(e) => setFormData({ ...formData, ad_name: e.target.value })}
                    placeholder="Internal name for this promotion"
                  />
                  {formErrors.ad_name && (
                    <p className="text-sm text-destructive mt-1">{formErrors.ad_name}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Business or company name"
                  />
                  {formErrors.company_name && (
                    <p className="text-sm text-destructive mt-1">{formErrors.company_name}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="offer_text">Offer Text *</Label>
                  <Textarea
                    id="offer_text"
                    value={formData.offer_text}
                    onChange={(e) => setFormData({ ...formData, offer_text: e.target.value })}
                    placeholder="Describe the promotion offer"
                    rows={3}
                  />
                  {formErrors.offer_text && (
                    <p className="text-sm text-destructive mt-1">{formErrors.offer_text}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="image_url">Image URL *</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/promo-image.jpg"
                  />
                  {formErrors.image_url && (
                    <p className="text-sm text-destructive mt-1">{formErrors.image_url}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <Label htmlFor="cta_url">CTA URL *</Label>
                  <Input
                    id="cta_url"
                    value={formData.cta_url}
                    onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                    placeholder="https://example.com/promo-landing"
                  />
                  {formErrors.cta_url && (
                    <p className="text-sm text-destructive mt-1">{formErrors.cta_url}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                  {formErrors.start_date && (
                    <p className="text-sm text-destructive mt-1">{formErrors.start_date}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                  {formErrors.end_date && (
                    <p className="text-sm text-destructive mt-1">{formErrors.end_date}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="page_placement">Page Placement *</Label>
                  <Select
                    value={formData.page_placement}
                    onValueChange={(value) => setFormData({ ...formData, page_placement: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select placement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promos_locales">Promos Locales</SelectItem>
                      <SelectItem value="home_banner">Home Banner</SelectItem>
                      <SelectItem value="sidebar">Sidebar</SelectItem>
                      <SelectItem value="footer">Footer</SelectItem>
                    </SelectContent>
                  </Select>
                  {formErrors.page_placement && (
                    <p className="text-sm text-destructive mt-1">{formErrors.page_placement}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Food, Retail, Services"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="target_locations">Target Locations</Label>
                  <Input
                    id="target_locations"
                    value={formData.target_locations}
                    onChange={(e) => setFormData({ ...formData, target_locations: e.target.value })}
                    placeholder="San Ysidro, Otay Mesa (comma-separated)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Comma-separated list of locations</p>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="discount, food, weekend (comma-separated)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Comma-separated list of tags</p>
                </div>

                <div>
                  <Label htmlFor="company_address">Company Address</Label>
                  <Input
                    id="company_address"
                    value={formData.company_address}
                    onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                    placeholder="123 Main St, City, State"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@example.com"
                  />
                  {formErrors.contact_email && (
                    <p className="text-sm text-destructive mt-1">{formErrors.contact_email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="company_website">Company Website</Label>
                  <Input
                    id="company_website"
                    value={formData.company_website}
                    onChange={(e) => setFormData({ ...formData, company_website: e.target.value })}
                    placeholder="https://company.com"
                  />
                  {formErrors.company_website && (
                    <p className="text-sm text-destructive mt-1">{formErrors.company_website}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="budget_usd">Budget (USD)</Label>
                  <Input
                    id="budget_usd"
                    type="number"
                    step="0.01"
                    value={formData.budget_usd}
                    onChange={(e) => setFormData({ ...formData, budget_usd: e.target.value })}
                    placeholder="1000.00"
                  />
                </div>

                <div>
                  <Label htmlFor="cost_per_click">Cost Per Click</Label>
                  <Input
                    id="cost_per_click"
                    type="number"
                    step="0.01"
                    value={formData.cost_per_click}
                    onChange={(e) => setFormData({ ...formData, cost_per_click: e.target.value })}
                    placeholder="0.50"
                  />
                </div>

                <div className="col-span-2 flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPromo ? "Update Promotion" : "Create Promotion"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {promotions.map((promo) => (
          <Card key={promo.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{promo.ad_name || promo.company_name}</CardTitle>
                  <CardDescription>{promo.company_name}</CardDescription>
                </div>
                <Badge variant={promo.is_active ? "default" : "secondary"}>
                  {promo.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {promo.image_url && (
                <img
                  src={promo.image_url}
                  alt={promo.company_name}
                  className="w-full h-32 object-cover rounded-md"
                />
              )}
              
              <p className="text-sm text-muted-foreground line-clamp-2">
                {promo.offer_text}
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDate(promo.start_date)} - {formatDate(promo.end_date)}</span>
                </div>
                
                {promo.target_locations && promo.target_locations.length > 0 && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="line-clamp-1">{promo.target_locations.join(", ")}</span>
                  </div>
                )}

                {promo.tags && promo.tags.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div className="flex flex-wrap gap-1">
                      {promo.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                <div className="text-center">
                  <div className="text-lg font-semibold">{promo.views || 0}</div>
                  <div className="text-xs text-muted-foreground">Views</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{promo.clicks || 0}</div>
                  <div className="text-xs text-muted-foreground">Clicks</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{promo.redemptions || 0}</div>
                  <div className="text-xs text-muted-foreground">Redemptions</div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleEdit(promo)}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDeletePromoId(promo.id);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {promotions.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">No promotions found. Create your first promotion to get started.</p>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Promotion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this promotion? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletePromoId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
